import { useCallback, useEffect, useRef, useState } from "react";
import {
  getPermanenceService,
  processPermanenceData,
} from "modules/came/services/permanenceService";
import {
  TICKET_TYPES,
  ticketValue,
  todayInBogota,
  validateFrequencyFilters,
} from "modules/came/model/frequencyModel";

const initialFilters = () => ({
  startDate: todayInBogota(),
  endDate: todayInBogota(),
  ticketTypes: [],
});
const usePermanencia = () => {
  const [filters, setFilters] = useState(initialFilters);
  const firstFilters = useRef(filters);
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [ticketTypeOptions, setTicketTypeOptions] = useState(TICKET_TYPES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [processedData, setProcessedData] = useState(null);
  const request = useRef(null);

  const loadData = useCallback(async (currentFilters) => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const current = () => request.current === controller && !controller.signal.aborted;
    const snapshot = { ...currentFilters, ticketTypes: [...currentFilters.ticketTypes] };
    setError(null);
    setProcessedData(null);
    setData([]);
    const invalid = validateFrequencyFilters(snapshot);
    if (invalid) {
      setError(invalid);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await getPermanenceService(snapshot, controller.signal);
      if (!current()) return;
      if (!result.success) throw new Error(result.error || "Error al consultar el informe.");
      const processed = processPermanenceData(result.data, snapshot);
      setData(result.data);
      setProcessedData(processed);
      setAppliedFilters(snapshot);
      setTicketTypeOptions((previous) => [
        ...new Map(
          [...previous, ...(result.ticketTypes || [])]
            .filter((item) => item?.value && item?.label)
            .map((item) => [
              ticketValue(item),
              { value: ticketValue(item), label: String(item.label) },
            ])
        ).values(),
      ]);
    } catch (err) {
      if (current()) setError(err.message || "No se pudo consultar el informe.");
    } finally {
      if (current()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(firstFilters.current);
    return () => {
      request.current?.abort();
      request.current = null;
    };
  }, [loadData]);
  const updateFilters = useCallback(
    (value) => setFilters((previous) => ({ ...previous, ...value })),
    []
  );
  const resetFilters = useCallback(() => {
    const next = initialFilters();
    setFilters(next);
    loadData(next);
  }, [loadData]);
  const applyFilters = useCallback(() => loadData(filters), [loadData, filters]);
  const refresh = useCallback(() => loadData(appliedFilters), [loadData, appliedFilters]);
  const getSummary = useCallback(
    () =>
      processedData
        ? {
            totalVisits: processedData.totalVisits,
            totalDays: processedData.totalDays,
          }
        : null,
    [processedData]
  );
  return {
    loading,
    error,
    data,
    processedData,
    filters,
    appliedFilters,
    ticketTypeOptions,
    updateFilters,
    resetFilters,
    refresh,
    applyFilters,
    getSummary,
    loadData,
  };
};
export default usePermanencia;
